import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AIQuizGeneratorWizard from "components/AIQuizGeneratorWizard";
import TranslateConfirmDialog from "components/TranslateConfirmDialog";
import { translateQuizQuestionCall } from "../../redux/api/quizTranslation.api";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "../../context";
import { store } from "../../redux/store";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { openAppConfirm } from "../../components/AppDialogs";
import {
  AddQuizContentByIdCall,
  DeleteQuizContentByIdCall,
  EditQuizContentByIdCall,
  GetAllQuizByIdCall,
  ResultQuizCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import { GetTitlesCall } from "../../redux/api/title.api";
import { generateQuestionCall } from "../../redux/api/ai.api";
import "./map.css";

const INPUT_HEIGHT_SX = { "& .MuiInputBase-root": { minHeight: 44 } };
const MENU_PROPS = { PaperProps: { sx: { maxHeight: 320, bgcolor: "background.paper", color: "text.primary" } } };
const QUESTION_TRUNCATE_LENGTH = 120;
const noImageUrl = require("../../assets/images/no-image.jpg");

function stripHtml(html) {
  if (typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function getTitleLabel(item, field = "title", lang = "en") {
  const list = item?.titleLocalizations?.filter((e) => e.field === field) || [];
  const match =
    list.find((e) => e.language === lang) ||
    list.find((e) => e.language === "en") ||
    list[0];
  return match?.value || "";
}
function getSlideImageUrl(subtitle) {
  if (subtitle?.image) return subtitle.image;
  const slide = subtitle?.slides?.[0];
  const file = slide?.Files?.[0] || slide?.files?.[0];
  const url = file?.url;
  if (url) return url + "/0/0/0.png";
  return null;
}
function getSlideTileUrl(entry) {
  if (!entry?.imageUrl) return "";
  return entry.imageUrl.replace(/\/3\/3\/3\.png$/i, "") + "/{z}/{x}/{y}.png";
}
function buildSlideEntries(titles, sourceLabel) {
  const entries = [];
  const is3D = sourceLabel === "3D Model";
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const fileUrl = file?.url;
      entries.push({
        tileId: tile.id,
        slideId: slide.id,
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        imageUrl: getSlideImageUrl(sub),
        source: sourceLabel,
        is3D: !!is3D,
        modelUrl: is3D && fileUrl ? fileUrl : null,
      });
    });
  });
  return entries;
}
function getPinLabel(index) {
  const labels = ["A", "B", "C", "D"];
  return labels[index] ?? String.fromCharCode(65 + index);
}
function createSlidePinIcon(label) {
  return L.divIcon({
    className: "slide-pin-marker-wrapper",
    html: `<div style="display:flex;flex-direction:column;align-items:center;line-height:1;pointer-events:auto;">
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:14px solid #28a745;"></div>
      <div style="background:#007bff;color:white;font-weight:bold;font-size:13px;border-radius:4px;padding:3px 8px;margin-top:2px;min-width:22px;text-align:center;box-sizing:border-box;">${label}</div>
    </div>`,
    iconSize: [24, 44],
    iconAnchor: [12, 0],
  });
}
function generateUniqueId() {
  return "id-" + Math.random().toString(36).substr(2, 16);
}
function looksLike3DUrl(url) {
  if (!url || typeof url !== "string") return false;
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".glb") || path.endsWith(".gltf");
}

const MAP_CONTAINER_ID = "general-quiz-slide-map";
const STUDENT_MAP_CONTAINER_ID = "student-general-quiz-map";

function ShowQuestions() {
  const { id, lang } = useParams();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const mapRef = useRef(null);

  const [quizTitleName, setQuizTitleName] = useState("");
  /* The category the current quiz title belongs to (histology/embryology
     /anatomy). Discovered from whichever GetQuizTitlesCall returns this
     id so the AI wizard can pre-select the matching category. */
  const [quizTitleType, setQuizTitleType] = useState("histology");
  const [aiBatchOpen, setAiBatchOpen] = useState(false);
  /* When non-null, the per-question translate confirmation dialog is
     open. We store the entire item so the dialog can show the
     question's title in the description. */
  const [translateTargetQuestion, setTranslateTargetQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [question, onChangeQuestion] = useState("");
  const [level, setLevel] = useState("1");
  const [points1, setPoints1] = useState(5);
  const [answer1, setAnswer1] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [answer4, setAnswer4] = useState("");
  const [titleIdForEdit, setTitleIdForEdit] = useState("");
  const [rightAnswer, setRightAnswer] = useState("");
  const [info, setInfo] = useState([]);
  const [tileURL, setTitleURL] = useState("");
  const [questionDialog, setQuestionDialog] = useState(false);
  const [dialogTransitionDone, setDialogTransitionDone] = useState(false);
  const [publicSlideEntries, setPublicSlideEntries] = useState([]);
  const [selectedSlideEntry, setSelectedSlideEntry] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAssistExpanded, setAiAssistExpanded] = useState(true);
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage, setQuestionsRowsPerPage] = useState(10);
  const [studentQuestionIndex, setStudentQuestionIndex] = useState(0);
  const [studentAnswers, setStudentAnswers] = useState({});
  const [studentSubmitted, setStudentSubmitted] = useState(false);

  const pinCountRef = useRef(0);
  const markersRef = useRef([]);
  const studentMapRef = useRef(null);
  const role = store.getState().auth?.role;
  const isStudentRole = String(role || "").toLowerCase() === "student";
  const studentUserId = store.getState().auth?.id || "anonymous";

  useEffect(() => {
    fetchQuestions();
  }, [id, lang]);

  useEffect(() => {
    /* The quiz title id in the URL can belong to any of the three
       categories (histology / embryology / anatomy). Look in all three so
       the AI wizard can resolve the title name + category correctly even
       for embryology/anatomy quizzes. */
    const loadQuizTitleName = async () => {
      const types = ["histology", "embryology", "anatomy"];
      for (const type of types) {
        try {
          const res = await GetQuizTitlesCall({ lang, type });
          const list = Array.isArray(res?.data) ? res.data : [];
          const found = list.find((t) => String(t.id) === String(id));
          if (found?.title) {
            setQuizTitleName(found.title);
            setQuizTitleType(type);
            return;
          }
        } catch (_) {
          /* try the next category */
        }
      }
    };
    loadQuizTitleName();
  }, [id, lang]);

  useEffect(() => {
    if (!questionDialog) return;
    const langVal = store.getState().general?.lang || lang || "en";
    const load = async () => {
      try {
        const [hisRes, embRes, threeRes] = await Promise.all([
          GetTitlesCall(langVal, "his"),
          GetTitlesCall(langVal, "emb"),
          GetTitlesCall(langVal, "three_d_model"),
        ]);
        const hisTitles = Array.isArray(hisRes) ? hisRes : [];
        const embTitles = Array.isArray(embRes) ? embRes : [];
        const threeTitles = Array.isArray(threeRes) ? threeRes : [];
        setPublicSlideEntries([
          ...buildSlideEntries(hisTitles, "Histology"),
          ...buildSlideEntries(embTitles, "Embryology"),
          ...buildSlideEntries(threeTitles, "3D Model"),
        ]);
      } catch {
        setPublicSlideEntries([]);
      }
    };
    load();
  }, [questionDialog, lang]);

  useEffect(() => {
    if (!questionDialog || publicSlideEntries.length === 0 || !tileURL) return;
    const entry = publicSlideEntries.find((e) =>
      e.is3D ? e.modelUrl === tileURL : getSlideTileUrl(e) === tileURL
    );
    setSelectedSlideEntry(entry || null);
  }, [questionDialog, publicSlideEntries, tileURL]);

  const is3DSelected = selectedSlideEntry?.is3D || looksLike3DUrl(tileURL);
  const slideTileUrl = !is3DSelected && tileURL ? tileURL : "";

  const fetchQuestions = async () => {
    const result = await GetAllQuizByIdCall(id, lang);
    setQuestions(Array.isArray(result) ? result : []);
  };

  const handleGenerateQuestion = async () => {
    setAiError(null);
    setAiLoading(true);
    try {
      const res = await generateQuestionCall({
        prompt: aiPrompt.trim(),
        title: quizTitleName || "",
        slide_context: selectedSlideEntry?.label || "",
        question_type: "text_only",
      });
      if (res && typeof res.status === "number" && res.status >= 400) {
        setAiError(res?.message || "Failed to generate question");
        return;
      }
      const data = res?.data ?? res;
      let answers = Array.isArray(data.answers) ? data.answers.slice(0, 4) : [];
      while (answers.length < 4) answers.push({ answer_text: "", is_correct: false });
      // Shuffle so correct answer isn't always A
      const shuffle = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };
      answers = shuffle(answers);
      const a1 = answers[0]?.answer_text ?? "";
      const a2 = answers[1]?.answer_text ?? "";
      const a3 = answers[2]?.answer_text ?? "";
      const a4 = answers[3]?.answer_text ?? "";
      const correctIdx = answers.findIndex((a) => a && a.is_correct);
      const rightAnswerIndex = correctIdx >= 0 ? correctIdx + 1 : 1;

      onChangeQuestion(data.question_content || "");
      setAnswer1(a1);
      setAnswer2(a2);
      setAnswer3(a3);
      setAnswer4(a4);
      setRightAnswer(String(rightAnswerIndex));
      const diff = (data.difficulty || "medium").toLowerCase();
      setLevel(diff === "easy" ? "0" : diff === "hard" ? "2" : "1");
      setAiPrompt("");
    } catch (e) {
      setAiError(e?.message || "Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  useLayoutEffect(() => {
    if (!questionDialog || !slideTileUrl || !dialogTransitionDone) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      pinCountRef.current = 0;
      return;
    }
    const el = document.getElementById(MAP_CONTAINER_ID);
    if (!el || mapRef.current) return;
    el.innerHTML = "";
    const map = L.map(MAP_CONTAINER_ID, {
      maxZoom: 10,
      zoomControl: false,
      maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      maxBoundsViscosity: 1,
    }).setView([-10.505, -0.09], 2);
    mapRef.current = map;
    L.tileLayer(slideTileUrl, {
      attribution: "&copy; Microlab",
      maxZoom: 10,
      noWrap: true,
      bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
    }).addTo(map);
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
      draw: {
        polyline: false,
        polygon: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: true,
      },
      edit: {
        featureGroup: drawnItems,
        edit: false,
        remove: true,
      },
    });
    map.addControl(drawControl);
    map.on("draw:created", (e) => {
      if (e.layerType !== "marker") return;
      const latlng = e.layer.getLatLng();
      e.layer.remove();
      const labelIndex = pinCountRef.current;
      const label = getPinLabel(labelIndex);
      const marker = L.marker(latlng, { icon: createSlidePinIcon(label) }).addTo(drawnItems);
      const geoJsonItem = {
        type: "Feature",
        geometry: { type: "Point", coordinates: [latlng.lng, latlng.lat] },
        properties: { id: generateUniqueId(), label },
      };
      setInfo((prev) => [...prev, geoJsonItem]);
      markersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
      pinCountRef.current += 1;
    });
    setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 100);
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      pinCountRef.current = 0;
    };
  }, [questionDialog, slideTileUrl, dialogTransitionDone]);

  useEffect(() => {
    if (!mapRef.current || !slideTileUrl) return;
    const pins = info;
    markersRef.current.forEach((m) => m.marker.remove());
    markersRef.current = [];
    pins.forEach((geoJsonItem) => {
      const [lng, lat] = geoJsonItem.geometry.coordinates;
      const label = geoJsonItem.properties.label;
      const marker = L.marker([lat, lng], { icon: createSlidePinIcon(label) }).addTo(mapRef.current);
      markersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
    });
    pinCountRef.current = pins.length;
    if (pins.length >= 2) {
      const bounds = L.latLngBounds(pins.map((f) => [f.geometry.coordinates[1], f.geometry.coordinates[0]]));
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
    }
  }, [info, slideTileUrl]);

  const deletePin = (geoJsonId) => {
    const markerObj = markersRef.current.find((m) => m.geoJsonId === geoJsonId);
    if (!markerObj) return;

    // Remove markers from map
    markerObj.marker.remove();

    markersRef.current = markersRef.current.filter(
      (m) => m.geoJsonId !== geoJsonId
    );

    const deletedLabel = info.find((item) => item.properties.id === geoJsonId)
      ?.properties.label;
    setInfo((prev) => prev.filter((item) => item.properties.id !== geoJsonId));

    if (deletedLabel === "A") setAnswer1("");
    else if (deletedLabel === "B") setAnswer2("");
    else if (deletedLabel === "C") setAnswer3("");
    else if (deletedLabel === "D") setAnswer4("");

    markersRef.current.forEach((m, i) => {
      m.marker.setIcon(createSlidePinIcon(getPinLabel(i)));
    });
    setInfo((prev) =>
      prev.map((item, idx) => ({
        ...item,
        properties: { ...item.properties, label: getPinLabel(idx) },
      }))
    );

    // Update pin counter
    pinCountRef.current = markersRef.current.length;
  };

  const openNewQuestion = () => {
    setTitleIdForEdit("");
    onChangeQuestion("");
    setAnswer1("");
    setAnswer2("");
    setAnswer3("");
    setAnswer4("");
    setRightAnswer("");
    setLevel("1");
    setPoints1(5);
    setTitleURL("");
    setInfo([]);
    setSelectedSlideEntry(null);
    setAiPrompt("");
    setAiError(null);
    setQuestionDialog(true);
  };

  const newQuestion = () => {
    setTitleIdForEdit("");
    onChangeQuestion("");
    setAnswer1("");
    setAnswer2("");
    setAnswer3("");
    setAnswer4("");
    setRightAnswer("");
    setLevel("1");
    setPoints1(5);
    setTitleURL("");
    setInfo([]);
    setSelectedSlideEntry(null);
  };

  const saveQuestion = async () => {
    if (!titleIdForEdit) {
      const body = {
        title_id: id,
        lang,
        title: "no title",
        content: question,
        answer_a: answer1,
        answer_b: answer2,
        answer_c: answer3,
        answer_d: answer4,
        right_answer: rightAnswer,
        point: points1,
        level: level,
        footage: tileURL ?? "",
        footage_type: tileURL ? "slide" : "",
        pins: JSON.stringify(info),
      };
      await AddQuizContentByIdCall(body);
    } else {
      const body = {
        quiz_id: titleIdForEdit,
        lang,
        title: "no title",
        content: question,
        answer_a: answer1,
        answer_b: answer2,
        answer_c: answer3,
        answer_d: answer4,
        right_answer: rightAnswer,
        point: points1,
        level: level,
        footage: tileURL ?? "",
        footage_type: tileURL ? "slide" : "",
        pins: JSON.stringify(info),
      };
      await EditQuizContentByIdCall(body);
    }
    setQuestionDialog(false);
    setDialogTransitionDone(false);
    fetchQuestions();
    newQuestion();
  };

  // Delete question
  const handleDelete = async (item, index) => {
    try {


      const response = await DeleteQuizContentByIdCall({ quiz_id: item.id });
      // Remove question locally
      fetchQuestions();
      alert("Question deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Error deleting question");
    }
  };

  const handleEdit = (item) => {
    onChangeQuestion(item?.content ?? "");
    setLevel(item?.level ?? "1");
    setPoints1(item?.point ?? 5);
    setAnswer1(item?.answer_a ?? "");
    setAnswer2(item?.answer_b ?? "");
    setAnswer3(item?.answer_c ?? "");
    setAnswer4(item?.answer_d ?? "");
    setRightAnswer(item?.right_answer ?? "");
    setTitleIdForEdit(item?.id ?? "");
    setTitleURL(item?.footage ?? "");
    try {
      const pinsArray = item?.pins ? (typeof item.pins === "string" ? JSON.parse(item.pins) : item.pins) : [];
      setInfo(Array.isArray(pinsArray) ? pinsArray : []);
    } catch {
      setInfo([]);
    }
    setQuestionDialog(true);
  };

  const isFormValid =
    answer1 && answer2 && answer3 && answer4 && rightAnswer && question;

  const paginatedQuestions = useMemo(() => {
    const start = questionsPage * questionsRowsPerPage;
    return questions.slice(start, start + questionsRowsPerPage);
  }, [questions, questionsPage, questionsRowsPerPage]);

  const handleQuestionsPageChange = (_, newPage) => setQuestionsPage(newPage);
  const handleQuestionsRowsPerPageChange = (e) => {
    setQuestionsRowsPerPage(parseInt(e.target.value, 10));
    setQuestionsPage(0);
  };

  useEffect(() => {
    setStudentQuestionIndex(0);
    setStudentAnswers({});
    setStudentSubmitted(false);
  }, [id, lang, questions.length]);


  const normalizeRightAnswer = (value) => {
    if (value == null) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "a") return "1";
    if (v === "b") return "2";
    if (v === "c") return "3";
    if (v === "d") return "4";
    return v;
  };

  const studentScore = useMemo(() => {
    if (!questions.length) return 0;
    let correct = 0;
    questions.forEach((q, idx) => {
      const key = q?.id || idx;
      const selected = String(studentAnswers[key] || "");
      const right = normalizeRightAnswer(q?.right_answer);
      if (selected && right && selected === right) correct += 1;
    });
    return correct;
  }, [questions, studentAnswers]);

  const currentStudentQuestion = questions[studentQuestionIndex] || null;
  const currentStudentQuestionKey =
    currentStudentQuestion?.id || studentQuestionIndex;
  const currentSelectedAnswer = studentAnswers[currentStudentQuestionKey] || "";
  const studentProgress = questions.length
    ? ((studentQuestionIndex + 1) / questions.length) * 100
    : 0;
  const studentMediaUrl = currentStudentQuestion?.footage || "";
  const studentMediaType = String(currentStudentQuestion?.footage_type || "").toLowerCase();
  const isStudent3D = looksLike3DUrl(studentMediaUrl);
  const isStudentSlide =
    !isStudent3D &&
    !!studentMediaUrl &&
    (studentMediaType === "slide" || /\{z\}\/\{x\}\/\{y\}\.png/i.test(studentMediaUrl));

  useEffect(() => {
    if (!isStudentRole || !isStudentSlide || !studentMediaUrl || studentSubmitted) {
      if (studentMapRef.current) {
        studentMapRef.current.remove();
        studentMapRef.current = null;
      }
      return;
    }

    const t = setTimeout(() => {
      const el = document.getElementById(STUDENT_MAP_CONTAINER_ID);
      if (!el) return;

      if (studentMapRef.current) {
        studentMapRef.current.remove();
        studentMapRef.current = null;
      }

      const map = L.map(STUDENT_MAP_CONTAINER_ID, {
        maxZoom: 10,
        maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
        maxBoundsViscosity: 1,
      }).setView([-10.505, -0.09], 2);

      L.tileLayer(studentMediaUrl, {
        attribution: "&copy; Microlab",
        maxZoom: 10,
        noWrap: true,
        bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      }).addTo(map);

      let pins = [];
      try {
        const parsed = currentStudentQuestion?.pins
          ? typeof currentStudentQuestion.pins === "string"
            ? JSON.parse(currentStudentQuestion.pins)
            : currentStudentQuestion.pins
          : [];
        pins = Array.isArray(parsed) ? parsed : [];
      } catch {
        pins = [];
      }

      pins.forEach((feature, idx) => {
        const coords = feature?.geometry?.coordinates;
        if (!coords || coords.length < 2) return;
        const label = feature?.properties?.label || getPinLabel(idx);
        L.marker([coords[1], coords[0]], { icon: createSlidePinIcon(label) }).addTo(map);
      });

      if (pins.length >= 2) {
        const bounds = L.latLngBounds(
          pins.map((f) => {
            const [lng, lat] = f?.geometry?.coordinates || [0, 0];
            return [lat, lng];
          })
        );
        if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20], maxZoom: 10 });
      }

      studentMapRef.current = map;
      setTimeout(() => {
        if (studentMapRef.current) studentMapRef.current.invalidateSize();
      }, 80);
    }, 80);

    return () => {
      clearTimeout(t);
      if (studentMapRef.current) {
        studentMapRef.current.remove();
        studentMapRef.current = null;
      }
    };
  }, [
    isStudentRole,
    isStudentSlide,
    studentMediaUrl,
    studentSubmitted,
    studentQuestionIndex,
    currentStudentQuestion?.pins,
  ]);

  if (isStudentRole) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox sx={{ p: { xs: 2, md: 3 }, maxWidth: 980, mx: "auto" }}>
          <MDBox mb={2}>
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary" }}>
              {quizTitleName || "General Quiz"}
            </MDTypography>
            <MDTypography variant="button" sx={{ color: "text.secondary" }}>
              Student mode - take quiz only
            </MDTypography>
          </MDBox>

          {questions.length === 0 ? (
            <Card sx={{ p: 2 }}>
              <MDTypography color="text">No questions available for this quiz.</MDTypography>
            </Card>
          ) : studentSubmitted ? (
            <Card sx={{ p: 2.5, borderRadius: 2 }}>
              <MDTypography variant="h5" fontWeight="bold" mb={1}>
                Quiz Completed
              </MDTypography>
              <MDTypography variant="body1" mb={2}>
                Score: {studentScore} / {questions.length} (
                {Math.round((studentScore / questions.length) * 100)}%)
              </MDTypography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setStudentQuestionIndex(0);
                  setStudentAnswers({});
                  setStudentSubmitted(false);
                }}
              >
                Retake Quiz
              </Button>
            </Card>
          ) : (
            <Card sx={{ p: 2.5, borderRadius: 2 }}>
              <MDBox
                sx={{
                  mb: 2,
                  height: 8,
                  borderRadius: 1,
                  bgcolor: "grey.300",
                  overflow: "hidden",
                }}
              >
                <MDBox
                  sx={{
                    height: "100%",
                    width: `${studentProgress}%`,
                    bgcolor: "info.main",
                    borderRadius: 1,
                    transition: "width 0.25s ease",
                  }}
                />
              </MDBox>
              <MDTypography variant="caption" color="text" mb={2} display="block">
                Question {studentQuestionIndex + 1} of {questions.length}
              </MDTypography>

              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.2,
                  bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", "& img": { maxWidth: "100%", height: "auto" } }}
                  dangerouslySetInnerHTML={{
                    __html: currentStudentQuestion?.content || "No question text",
                  }}
                />
              </Box>

              {studentMediaUrl ? (
                <MDBox mb={2}>
                  {isStudent3D ? (
                    <MDBox
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.2,
                        overflow: "hidden",
                        height: 360,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                      }}
                    >
                      <iframe
                        src={`${
                          typeof window !== "undefined" ? window.location.origin : ""
                        }/model-viewer.html?src=${encodeURIComponent(studentMediaUrl)}&theme=${
                          darkMode ? "dark" : "light"
                        }`}
                        title="3D Model"
                        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                      />
                    </MDBox>
                  ) : isStudentSlide ? (
                    <MDBox
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.2,
                        overflow: "hidden",
                        height: 320,
                        minHeight: 320,
                      }}
                    >
                      <div
                        id={STUDENT_MAP_CONTAINER_ID}
                        style={{ width: "100%", height: 320, minHeight: 320 }}
                      />
                    </MDBox>
                  ) : (
                    <MDBox
                      component="img"
                      src={studentMediaUrl}
                      alt="Question media"
                      sx={{
                        width: "100%",
                        maxHeight: 320,
                        objectFit: "contain",
                        borderRadius: 1.2,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
                      }}
                    />
                  )}
                </MDBox>
              ) : null}

              <Grid container spacing={1}>
                {[
                  { key: "1", text: currentStudentQuestion?.answer_a },
                  { key: "2", text: currentStudentQuestion?.answer_b },
                  { key: "3", text: currentStudentQuestion?.answer_c },
                  { key: "4", text: currentStudentQuestion?.answer_d },
                ].map((opt) => (
                  <Grid item xs={12} key={opt.key}>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={(t) => ({
                        justifyContent: "flex-start",
                        textTransform: "none",
                        py: 1.2,
                        px: 1.2,
                        borderRadius: 1.2,
                        borderWidth: 1.4,
                        borderColor:
                          currentSelectedAnswer === opt.key
                            ? t.palette.info.main
                            : alpha(t.palette.text.primary, 0.18),
                        bgcolor:
                          currentSelectedAnswer === opt.key
                            ? alpha(t.palette.info.main, 0.12)
                            : t.palette.background?.paper ?? "#fff",
                        color: t.palette.text.primary,
                        "&:hover": {
                          borderColor: t.palette.info.main,
                          bgcolor:
                            currentSelectedAnswer === opt.key
                              ? alpha(t.palette.info.main, 0.16)
                              : alpha(t.palette.info.main, 0.05),
                        },
                      })}
                      onClick={() =>
                        setStudentAnswers((prev) => ({
                          ...prev,
                          [currentStudentQuestionKey]: opt.key,
                        }))
                      }
                    >
                      <Box
                        sx={(t) => ({
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          mr: 1.2,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 12,
                          border: "1px solid",
                          borderColor:
                            currentSelectedAnswer === opt.key
                              ? t.palette.info.main
                              : alpha(t.palette.text.primary, 0.25),
                          bgcolor:
                            currentSelectedAnswer === opt.key
                              ? t.palette.info.main
                              : "transparent",
                          color:
                            currentSelectedAnswer === opt.key
                              ? "#fff"
                              : t.palette.text.primary,
                          flexShrink: 0,
                        })}
                      >
                        {String.fromCharCode(64 + Number(opt.key))}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight:
                            currentSelectedAnswer === opt.key ? 700 : 500,
                          color: "text.primary",
                          textAlign: "left",
                          width: "100%",
                          lineHeight: 1.4,
                        }}
                      >
                        {opt.text || "-"}
                      </Typography>
                    </Button>
                  </Grid>
                ))}
              </Grid>

              <MDBox mt={2} display="flex" justifyContent="space-between" gap={1}>
                <Button
                  disabled={studentQuestionIndex === 0}
                  onClick={() => setStudentQuestionIndex((v) => Math.max(0, v - 1))}
                >
                  Previous
                </Button>
                {studentQuestionIndex < questions.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={() =>
                      setStudentQuestionIndex((v) =>
                        Math.min(questions.length - 1, v + 1)
                      )
                    }
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={async () => {
                      const answerList = questions.map((q, idx) => {
                        const key = q?.id || idx;
                        return String(studentAnswers[key] || "");
                      });
                      const questionIds = questions.map((q, idx) => q?.id || idx);
                      const body = {
                        user_id: studentUserId,
                        quiz_ids: JSON.stringify(questionIds),
                        questions: JSON.stringify(questions),
                        answers: JSON.stringify(answerList),
                        score: Number(studentScore * 5),
                        wrong_count: Math.max(0, questions.length - studentScore),
                        right_count: studentScore,
                        time_taken: "30",
                        is_prof_quiz: false,
                      };
                      await ResultQuizCall(body);
                      setStudentSubmitted(true);
                    }}
                  >
                    Submit Quiz
                  </Button>
                )}
              </MDBox>
            </Card>
          )}
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        {/* Hero header — title meta + prominent AI CTA */}
        <Card
          sx={(t) => ({
            mb: 3,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: t.palette.divider,
            background: `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.10)} 0%, ${alpha(t.palette.info.main, 0.06)} 100%)`,
          })}
        >
          <MDBox
            sx={{
              p: { xs: 2, md: 3 },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography
                variant="caption"
                sx={{ color: "text.secondary", letterSpacing: 0.6, textTransform: "uppercase" }}
              >
                Quiz Questions
              </MDTypography>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: "text.primary",
                  mt: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={quizTitleName || "Questions"}
              >
                {quizTitleName || "Questions"}
              </MDTypography>
              <MDBox display="flex" gap={1} mt={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`${questions.length} question${questions.length === 1 ? "" : "s"}`}
                  sx={(t) => ({
                    bgcolor: alpha(t.palette.primary.main, 0.12),
                    color: "text.primary",
                    fontWeight: 600,
                  })}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Locale: ${String(lang || "en").toUpperCase()}`}
                  sx={{ color: "text.secondary" }}
                />
              </MDBox>
            </MDBox>

            <MDBox
              display="flex"
              gap={1.25}
              flexWrap="wrap"
              sx={{ flexShrink: 0 }}
            >
              <Button
                onClick={() => setAiBatchOpen(true)}
                startIcon={<AutoAwesomeRoundedIcon />}
                size="large"
                sx={{
                  textTransform: "none",
                  px: 2.5,
                  py: 1.25,
                  fontWeight: 700,
                  color: "#fff",
                  borderRadius: 2,
                  boxShadow: "0 8px 24px rgba(124, 58, 237, 0.35)",
                  background:
                    "linear-gradient(135deg, #5B4BFF 0%, #7C3AED 50%, #A855F7 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #4F40E8 0%, #6B2DDD 50%, #9445E5 100%)",
                    boxShadow: "0 10px 28px rgba(124, 58, 237, 0.45)",
                  },
                }}
              >
                Generate with AI
              </Button>
              <Button
                onClick={openNewQuestion}
                size="large"
                sx={(t) => ({
                  textTransform: "none",
                  px: 2.5,
                  py: 1.25,
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: t.palette.background?.paper ?? "#fff",
                  color: t.palette.primary.main,
                  border: "2px solid",
                  borderColor: t.palette.primary.main,
                  boxShadow: `0 4px 12px ${alpha(t.palette.primary.main, 0.15)}`,
                  "&:hover": {
                    bgcolor: alpha(t.palette.primary.main, 0.08),
                    borderColor: t.palette.primary.main,
                    boxShadow: `0 6px 16px ${alpha(t.palette.primary.main, 0.22)}`,
                  },
                })}
              >
                Add manually
              </Button>
            </MDBox>
          </MDBox>
        </Card>

        {/* Empty state: when there are zero questions, give the admin a
            big, friendly entry into the AI flow. */}
        {questions.length === 0 && (
          <Card
            sx={(t) => ({
              mb: 3,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              textAlign: "center",
              border: "1px dashed",
              borderColor: alpha(t.palette.primary.main, 0.4),
              bgcolor: alpha(t.palette.primary.main, 0.04),
            })}
          >
            <MDBox
              sx={(t) => ({
                width: 64,
                height: 64,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.18)}, ${alpha(t.palette.info.main, 0.18)})`,
              })}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 32, color: "primary.main" }} />
            </MDBox>
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary", mb: 1 }}>
              Build this quiz with AI
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "text.secondary", mb: 2.5, maxWidth: 560, mx: "auto" }}>
              Upload a PDF, PowerPoint, image, or audio of your lecture — or just
              describe what you want — and we'll draft multiple-choice questions
              you can review, edit, and save into <strong>{quizTitleName || "this title"}</strong>.
            </MDTypography>
            <MDBox display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
              <Button
                onClick={() => setAiBatchOpen(true)}
                startIcon={<AutoAwesomeRoundedIcon />}
                size="large"
                sx={{
                  textTransform: "none",
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  color: "#fff",
                  borderRadius: 2,
                  boxShadow: "0 8px 24px rgba(124, 58, 237, 0.35)",
                  background:
                    "linear-gradient(135deg, #5B4BFF 0%, #7C3AED 50%, #A855F7 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #4F40E8 0%, #6B2DDD 50%, #9445E5 100%)",
                  },
                }}
              >
                Generate with AI
              </Button>
              <Button
                onClick={openNewQuestion}
                size="large"
                sx={(t) => ({
                  textTransform: "none",
                  px: 3,
                  py: 1.25,
                  fontWeight: 700,
                  borderRadius: 2,
                  bgcolor: t.palette.background?.paper ?? "#fff",
                  color: t.palette.primary.main,
                  border: "2px solid",
                  borderColor: t.palette.primary.main,
                  boxShadow: `0 4px 12px ${alpha(t.palette.primary.main, 0.15)}`,
                  "&:hover": {
                    bgcolor: alpha(t.palette.primary.main, 0.08),
                    borderColor: t.palette.primary.main,
                  },
                })}
              >
                Add manually
              </Button>
            </MDBox>
          </Card>
        )}

        {paginatedQuestions.map((item, indexInPage) => {
          const realIndex = questionsPage * questionsRowsPerPage + indexInPage;
          const rawContent = item?.content || "";
          const plainText = stripHtml(rawContent);
          const isLong = plainText.length > QUESTION_TRUNCATE_LENGTH;
          const displayText = isLong ? plainText.slice(0, QUESTION_TRUNCATE_LENGTH) + "..." : plainText;
          return (
            <Card
              key={item.id || realIndex}
              sx={(t) => ({
                mb: 2,
                borderRadius: 2,
                boxShadow: 2,
                overflow: "hidden",
                bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default,
                color: t.palette.text.primary,
                border: "1px solid",
                borderColor: t.palette.divider,
              })}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Tooltip title={plainText || "No question text"} placement="top-start" enterDelay={300}>
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: 1.5,
                        bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                        borderRadius: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography variant="body2" component="span" sx={{ color: "text.primary" }}>
                        {displayText || "—"}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <MDBox display="flex" alignItems="center" gap={0.25} flexShrink={0}>
                    <Tooltip title="Translate this question to all supported languages">
                      <IconButton
                        size="small"
                        onClick={() => setTranslateTargetQuestion(item)}
                        sx={{ color: "primary.main" }}
                        aria-label="Translate question to all languages"
                      >
                        <TranslateRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete question">
                      <IconButton
                        size="small"
                        onClick={async () => {
                          const ok = await openAppConfirm({
                            title: "Delete question?",
                            message: "Delete this question?",
                            confirmText: "Delete",
                            confirmColor: "error",
                          });
                          if (ok) handleDelete(item, realIndex);
                        }}
                        sx={{ color: "error.main" }}
                        aria-label="Delete question"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit question (pins on slide)">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(item)}
                        sx={{ color: "text.secondary" }}
                        aria-label="Edit question"
                      >
                        <PushPinOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>
              </CardContent>
            </Card>
          );
        })}

        {questions.length > 0 && (
          <Box sx={(t) => ({ borderTop: 1, borderColor: t.palette.divider, bgcolor: t.palette.background?.card ?? t.palette.background?.paper, borderRadius: 1, mt: 1 })}>
            <TablePagination
              component="div"
              count={questions.length}
              page={questionsPage}
              onPageChange={handleQuestionsPageChange}
              rowsPerPage={questionsRowsPerPage}
              onRowsPerPageChange={handleQuestionsRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={(t) => ({
                color: t.palette.text.primary,
                "& .MuiTablePagination-selectIcon": { color: t.palette.text.primary },
                "& .MuiTablePagination-select": { color: t.palette.text.primary },
              })}
            />
          </Box>
        )}

        {/* Add / Edit Question Dialog — same UI as prof QuizSystem */}
        <Dialog
          open={questionDialog}
          onClose={() => { setQuestionDialog(false); setDialogTransitionDone(false); }}
          maxWidth="md"
          fullWidth
          TransitionProps={{ onEntered: () => setDialogTransitionDone(true) }}
          PaperProps={{
            sx: (t) => ({
              // Dark theme uses background.card (no .paper); light uses .paper
              bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default,
              color: t.palette.text.primary,
              border: "1px solid",
              borderColor: t.palette.divider,
            }),
          }}
        >
          <DialogTitle sx={{ color: "text.primary" }}>{titleIdForEdit ? "Edit question" : "Add question"}</DialogTitle>
          <DialogContent sx={(t) => ({ bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default, color: t.palette.text.primary })}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* AI Assist */}
              <Grid item xs={12}>
                <MDBox sx={{ borderRadius: 2, border: "1px solid", borderColor: alpha(theme.palette.primary.main, 0.3), bgcolor: alpha(theme.palette.primary.main, 0.04), overflow: "hidden" }}>
                  <Button fullWidth onClick={() => setAiAssistExpanded((e) => !e)} sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.5, px: 2, color: "text.primary", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
                    <AutoAwesomeRoundedIcon sx={{ mr: 1, color: "primary.main" }} />
                    <MDTypography variant="button" fontWeight="medium">AI Assist — generate question from title & slide</MDTypography>
                    <Box sx={{ flex: 1 }} />
                    <MDTypography variant="caption" sx={{ color: "text.secondary" }}>{aiAssistExpanded ? "Hide" : "Show"}</MDTypography>
                  </Button>
                  <Collapse in={aiAssistExpanded}>
                    <MDBox sx={{ px: 2, pb: 2, pt: 0 }}>
                      <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>Select a slide below (optional) then describe what you want or leave empty.</MDTypography>
                      <TextField fullWidth multiline minRows={2} placeholder="e.g. Ask about the structure at point A" value={aiPrompt} onChange={(e) => { setAiPrompt(e.target.value); setAiError(null); }} size="small" sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
                      {aiError && <MDTypography variant="caption" sx={{ color: "error.main", display: "block", mb: 1 }}>{aiError}</MDTypography>}
                      <Button variant="contained" color="primary" onClick={handleGenerateQuestion} disabled={aiLoading} startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />} sx={{ textTransform: "none" }}>{aiLoading ? "Generating…" : "Generate and fill form"}</Button>
                    </MDBox>
                  </Collapse>
                </MDBox>
              </Grid>

              {/* Slide dropdown — all slides + 3D */}
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" sx={{ color: "text.primary", display: "block", mb: 1 }}>Slide or 3D model (optional)</MDTypography>
                <Autocomplete
                  options={publicSlideEntries}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : "")}
                  value={selectedSlideEntry || null}
                  ListboxProps={{ sx: { maxHeight: 320 } }}
                  onChange={(e, val) => {
                    setSelectedSlideEntry(val);
                    if (!val) { setTitleURL(""); setInfo([]); return; }
                    if (val.is3D && val.modelUrl) { setTitleURL(val.modelUrl); setInfo([]); return; }
                    setTitleURL(getSlideTileUrl(val));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" placeholder="Search slides…" sx={{ ...INPUT_HEIGHT_SX, "& .MuiInputBase-root": { ...INPUT_HEIGHT_SX["& .MuiInputBase-root"], bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} InputProps={{ ...params.InputProps, startAdornment: <><InputAdornment position="start">🔍</InputAdornment>{params.InputProps.startAdornment}</> }} />
                  )}
                  renderOption={(props, opt) => (
                    <li {...props} key={`${opt.tileId}-${opt.slideId}`}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {opt.is3D && opt.modelUrl ? (
                          <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: "hidden", flexShrink: 0, bgcolor: (t) => alpha(t.palette.text.primary, 0.12), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "text.secondary" }}>3D</Box>
                        ) : (
                          <Box component="img" src={opt.imageUrl || noImageUrl} alt="" sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }} />
                        )}
                        <span>{opt.label}</span>
                      </Box>
                    </li>
                  )}
                />
              </Grid>

              {/* Map or 3D preview + pins */}
              {(selectedSlideEntry || tileURL) && (
                <Grid item xs={12}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                    <MDTypography variant="caption" fontWeight="medium" sx={{ color: "text.primary" }}>
                      {is3DSelected ? "Selected 3D model" : "Selected slide — add or delete pins, then save question"}
                    </MDTypography>
                    {!is3DSelected && (
                      <Button size="small" variant="outlined" color="secondary" onClick={() => setInfo([])} disabled={!info.length}>Clear pins</Button>
                    )}
                  </MDBox>
                  {is3DSelected && selectedSlideEntry?.modelUrl ? (
                    <MDBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: 380, bgcolor: (t) => alpha(t.palette.text.primary, 0.06) }}>
                      <iframe src={`${typeof window !== "undefined" ? window.location.origin : ""}/model-viewer.html?src=${encodeURIComponent(selectedSlideEntry.modelUrl)}&theme=${darkMode ? "dark" : "light"}`} title="3D" style={{ width: "100%", height: "100%", minHeight: 360, border: "none", display: "block" }} />
                    </MDBox>
                  ) : slideTileUrl ? (
                    <MDBox sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: 380, minHeight: 380 }}>
                      <div id={MAP_CONTAINER_ID} style={{ width: "100%", height: 380, minHeight: 380 }} />
                    </MDBox>
                  ) : null}
                  {!is3DSelected && info.length > 0 && (
                    <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {info.map((item) => (
                        <Chip key={item.properties.id} label={`Pin ${item.properties.label}`} onDelete={() => deletePin(item.properties.id)} size="small" color="primary" variant="outlined" />
                      ))}
                    </MDBox>
                  )}
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={INPUT_HEIGHT_SX} size="small">
                  <InputLabel sx={{ color: "text.secondary" }}>Level</InputLabel>
                  <Select value={level} onChange={(e) => setLevel(e.target.value)} label="Level" MenuProps={MENU_PROPS} sx={{ color: "text.primary", "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }}>
                    <MenuItem value="0">Easy</MenuItem>
                    <MenuItem value="1">Medium</MenuItem>
                    <MenuItem value="2">Hard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="number" label="Points" value={points1} onChange={(e) => setPoints1(e.target.value)} size="small" sx={{ ...INPUT_HEIGHT_SX, "& .MuiInputBase-root": { ...INPUT_HEIGHT_SX["& .MuiInputBase-root"], bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Question *" value={question} onChange={(e) => onChangeQuestion(e.target.value)} size="small" sx={{ "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
              </Grid>
              {[["Answer 1", answer1, setAnswer1], ["Answer 2", answer2, setAnswer2], ["Answer 3", answer3, setAnswer3], ["Answer 4", answer4, setAnswer4]].map(([lbl, val, setVal], idx) => (
                <Grid item xs={12} key={idx}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Chip size="small" color={rightAnswer === String(idx + 1) ? "success" : "default"} label={`${["A", "B", "C", "D"][idx]} ${rightAnswer === String(idx + 1) ? "(correct)" : ""}`} onClick={() => setRightAnswer(String(idx + 1))} sx={{ color: "text.primary" }} />
                    <TextField fullWidth size="small" label={lbl} value={val} onChange={(e) => setVal(e.target.value)} sx={{ "& .MuiInputBase-root": { bgcolor: "background.default", color: "text.primary" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" } }} />
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={(t) => ({ color: t.palette.text.primary, bgcolor: t.palette.background?.card ?? t.palette.background?.paper ?? t.palette.background?.default, borderTop: 1, borderColor: t.palette.divider })}>
            <Button onClick={() => { setQuestionDialog(false); setDialogTransitionDone(false); }} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={saveQuestion} disabled={!isFormValid} sx={{ color: "white" }}>Save</Button>
          </DialogActions>
        </Dialog>
        <AIQuizGeneratorWizard
          open={aiBatchOpen}
          onClose={() => setAiBatchOpen(false)}
          mode="general"
          lang={lang}
          defaultTitleId={id}
          defaultTitleName={quizTitleName}
          defaultCategory={quizTitleType}
          onSaved={() => {
            fetchQuestions();
          }}
        />
        <TranslateConfirmDialog
          open={!!translateTargetQuestion}
          onClose={() => setTranslateTargetQuestion(null)}
          title="Translate this question"
          description={
            translateTargetQuestion ? (
              <>
                The question titled{" "}
                <strong>
                  {(translateTargetQuestion.title ||
                    stripHtml(translateTargetQuestion.content || "")
                      .slice(0, 80)) ||
                    "(untitled)"}
                </strong>{" "}
                will be translated into every supported language using AI.
              </>
            ) : null
          }
          targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
          itemSummaryLabel="1 question"
          confirmLabel="Translate to all languages"
          onConfirm={async ({ setStatus }) => {
            setStatus("Translating question into every language…");
            const res = await translateQuizQuestionCall(
              translateTargetQuestion?.id,
            );
            return res;
          }}
          summaryRenderer={(res) => (
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Translated into {res?.targetLangs?.length ?? 0} language
                {res?.targetLangs?.length === 1 ? "" : "s"} ·{" "}
                {res?.created ?? 0} new · {res?.updated ?? 0} updated.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(res?.results || []).map((r) => (
                  <Chip
                    key={r.lang}
                    size="small"
                    color={r.action === "created" ? "success" : "info"}
                    variant="outlined"
                    label={`${r.lang.toUpperCase()} · ${r.action}`}
                  />
                ))}
              </Box>
            </Box>
          )}
        />
      </MDBox>
    </DashboardLayout>
  );
}

export default ShowQuestions;
